import * as React from 'react';
import axios from 'axios';
import { TextField } from '@origin-digital/style-guide';
import { SearchIcon } from '@origin-digital/icon';
import { AddressLookup, QAS_PROFILES } from '@origin-digital/utils/dist/cjs/qas-lookup';

import logger from '../../../../logging';
import analytics from '../../../../analytics';

import {
  AddressLookupService,
  QASAddressLookUpObject,
  QASAddressLookUpProps,
  QASAddressLookUpState,
  QASSearchResult
} from './QASAddressLookUp.types';

import * as Utils from './helpers/QASAddressLookUp.helper';
import Suggestions from './Suggestions';

class QASAddressLookUp extends React.Component<QASAddressLookUpProps, QASAddressLookUpState> {
  public static getDerivedStateFromProps(
    nextProps: QASAddressLookUpProps,
    prevState: QASAddressLookUpState
  ): Partial<QASAddressLookUpState | null> {
    if (nextProps.validating !== prevState.validating) {
      return { validating: nextProps.validating };
    } else {
      return null;
    }
  }

  private addressLookupService: AddressLookupService;
  private timeout: NodeJS.Timer | null;

  constructor(props: QASAddressLookUpProps) {
    super(props);

    this.state = {
      qasSearchResult: null,
      query: '',
      suggestions: [],
      validating: false
    };

    this.addressLookupService = AddressLookup({
      ajaxExecutor: axios,
      authToken: this.props.qasAuthToken
    });

    this.timeout = null;
  }

  public componentDidMount() {
    const { value, showManualAddress } = this.props;

    if (!showManualAddress) {
      this.setAddress(value);
    }
  }

  public componentDidUpdate(prevProps: QASAddressLookUpProps, prevState: QASAddressLookUpState) {
    const { validating, query } = this.state;
    const {
      value,
      autoFindMatchingAddresses,
      qasProfile,
      setAutoFindMatchingAddresses
    } = this.props;

    if (prevProps.validating !== prevState.validating) {
      this.setState({
        validating
      });
    }

    if (autoFindMatchingAddresses && typeof value === 'string' && query === '') {
      this.setAddress(value);
      this.addressLookupService
        .getMatchingAddresses(value, QAS_PROFILES[qasProfile])
        .then(response => {
          const suggestions: any[] = response.slice(0, 10);
          const accountAddress = suggestions.filter(
            addressObject =>
              addressObject.label
                .toLowerCase()
                .replace(/ {1+}/g, ' ')
                .trim() ===
              value
                .toLowerCase()
                .replace(/ {1+}/g, ' ')
                .trim()
          );
          if (accountAddress[0] === undefined) {
            this.onClick(suggestions[0]);
          } else {
            this.onClick(accountAddress[0]);
          }
          setAutoFindMatchingAddresses(false);
        })
        .catch(error => {
          logger.error('QASAddressLookUp.componentDidUpdate', error);
        });
    }
  }

  public componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  public render(): JSX.Element {
    const {
      disabled,
      error,
      label,
      name,
      helpText,
      width,
      touched,
      showManualAddress,
      manualAddressPlaceholder,
      'data-id': dataId
    } = this.props;
    const { query, validating } = this.state;
    const showError = disabled ? '' : error;
    const labelType = showManualAddress ? manualAddressPlaceholder : label;

    return (
      <TextField
        type="text"
        name={name}
        value={query}
        label={labelType}
        icon={SearchIcon}
        helpText={helpText}
        // @ts-ignore
        error={showError}
        data-id={dataId}
        // @ts-ignore
        touched={touched}
        disabled={disabled}
        spinner={validating}
        autoComplete="off"
        onChange={this.onChange}
        onBlur={this.onBlur}
        width={width}
        onClick={() => analytics.firstInteraction()}
        renderOverlay={this.suggestionDropDown}
      />
    );
  }

  private onBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const { onBlur } = this.props;

    if (onBlur) {
      onBlur(event);
    }
  };

  private onChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const { onChange } = this.props;
    const query = event.currentTarget.value;

    onChange(undefined);
    this.setState({ query }, () => {
      if (Utils.isQuerySearchable(query)) {
        this.getMatchingAddresses(query);
      } else {
        this.removeSuggestions();
      }
    });
  };

  private onClick = (result: QASSearchResult): void => {
    const { onAddressSelect, onEnterAddressManually } = this.props;

    if (result && result.addressId) {
      onAddressSelect(result);
      this.setState(
        {
          qasSearchResult: result,
          query: result.label,
          suggestions: []
        },
        () => {
          this.parseAddress(result.addressId);
        }
      );
    } else {
      this.setState({
        query: '',
        suggestions: []
      });
      onEnterAddressManually();
    }
  };

  private getMatchingAddresses = (query: string): void => {
    const { qasProfile, allowManualAddressEntry, manualAddressPlaceholder } = this.props;

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      const MAX_RESULTS = 10;
      let suggestions: any[];
      this.addressLookupService
        .getMatchingAddresses(query, QAS_PROFILES[qasProfile])
        .then(response => {
          suggestions = response.slice(0, MAX_RESULTS);
          if (suggestions.length <= 3 && allowManualAddressEntry) {
            suggestions.unshift({
              addressId: null,
              label: manualAddressPlaceholder,
              postcode: undefined
            });
          }
          this.setState({
            suggestions
          });
        })
        .catch(error => {
          logger.error('QASAddressLookUp.getMatchingAddresses', error);
        });
    }, 400);
  };

  private parseAddress = (addressId: string): void => {
    const { onChange, layout = 'Origin' } = this.props;
    const { qasSearchResult } = this.state;

    this.addressLookupService
      .parseAddress(addressId, layout)
      .then(response => {
        onChange({
          qasAddress: response,
          qasSearchResult
        });
      })
      .catch(error => {
        logger.error('QASAddressLookUp.parseAddress', error);
      });
  };

  private removeSuggestions = (): void => {
    this.setState({ suggestions: [] });
  };

  private setAddress = (address?: QASAddressLookUpObject | string): void => {
    const { onChange } = this.props;

    if (address !== undefined && typeof address !== 'string') {
      this.setState(
        {
          qasSearchResult: address.qasSearchResult,
          query: Utils.getQueryValue(address)
        },
        () => {
          onChange({
            qasAddress: address.qasAddress,
            qasSearchResult: address.qasSearchResult
          });
        }
      );
    }
    if (typeof address === 'string') {
      this.setState({
        query: address
      });
    }
  };

  private suggestionDropDown = (): JSX.Element => {
    const { query, suggestions } = this.state;

    return !suggestions.length ? (
      <React.Fragment />
    ) : (
      <Suggestions
        suggestions={suggestions}
        query={query}
        onClick={(e, result) => this.onClick(result)}
      />
    );
  };
}

export { QASAddressLookUp as default };
